/**
 * LogoTextBlock: `logo-text` content block.
 *
 * data: { imageUrl, imageAlt, heading, body, buttonText?, buttonLink?, reversed? }
 *
 * Two-column layout: image left, heading + body + optional CTA right (or the
 * reverse when `reversed` is set). Stacks to a single column on mobile
 * (col-md-6 breakpoint). `buttonText` and `buttonLink` are optional — both
 * must be present for the button to render. The button uses the same URL
 * routing logic as CtaButtonBlock: http(s) / mailto / tel render as a plain
 * <a>; relative paths go through React Router <Link>.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import {
    isAllowedImageUrl,
    isAllowedLinkUrl,
    isRawAnchorUrl,
    isExternalHttpUrl,
} from '../utils/urlValidator';

export default function LogoTextBlock({ data }) {
    const {
        imageUrl = '',
        imageAlt = '',
        heading = '',
        body = '',
        buttonText = '',
        buttonLink = '',
        reversed = false,
    } = data;

    const hasImage = imageUrl && isAllowedImageUrl(imageUrl);
    const hasButton = buttonText && buttonLink && isAllowedLinkUrl(buttonLink);

    return (
        <Row
            className={`align-items-center logo-text-block${reversed ? ' flex-row-reverse' : ''}`}
        >
            <Col xs={12} md={6} className="logo-text-block__image-col">
                {hasImage && (
                    <img
                        src={imageUrl}
                        alt={imageAlt}
                        className="logo-text-block__image img-fluid rounded"
                    />
                )}
            </Col>
            <Col xs={12} md={6} className="logo-text-block__text-col">
                {heading && (
                    <h3 className="content-heading--h3 logo-text-block__heading">
                        {heading}
                    </h3>
                )}
                {body && <p className="logo-text-block__body">{body}</p>}
                {hasButton &&
                    (isRawAnchorUrl(buttonLink) ? (
                        <Button
                            href={buttonLink}
                            {...(isExternalHttpUrl(buttonLink) && {
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            })}
                            variant="outline-primary"
                            size="lg"
                            className="rounded-pill px-4 mt-2"
                        >
                            {buttonText}
                        </Button>
                    ) : (
                        <Button
                            as={Link}
                            to={buttonLink}
                            variant="outline-primary"
                            size="lg"
                            className="rounded-pill px-4 mt-2"
                        >
                            {buttonText}
                        </Button>
                    ))}
            </Col>
        </Row>
    );
}

LogoTextBlock.propTypes = {
    data: PropTypes.shape({
        imageUrl: PropTypes.string,
        imageAlt: PropTypes.string,
        heading: PropTypes.string,
        body: PropTypes.string,
        buttonText: PropTypes.string,
        buttonLink: PropTypes.string,
        reversed: PropTypes.bool,
    }),
};

LogoTextBlock.defaultProps = {
    data: {},
};
